import { limparTokensPreview } from './previewTokenCache';

export const getToken = () => localStorage.getItem('token');

/** Remove todos os dados locais da sessão (token, perfil e acesso mestre). */
export const limparSessaoLocal = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('isAdmin');
  localStorage.removeItem('userId');
  sessionStorage.removeItem('masterToken');
  // Tokens de preview carregam `isAdmin`: sem esta limpeza um token emitido
  // para o admin sobrevivia ao logout/login e a grade do cliente pedia as
  // miniaturas como admin — bytes **sem marca d'água** cacheados como
  // `immutable` na URL do cliente.
  limparTokensPreview();
};

export const isUserAdmin = () => {
  const token = getToken();
  if (!token) return false;

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.isAdmin === true;
  } catch {
    return false;
  }
};

export const getUserId = () => {
  const token = getToken();
  if (!token) return null;

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.id;
  } catch {
    return null;
  }
};

// ── Acesso Mestre ──────────────────────────────────────────────────────────────

/**
 * Salva o token de acesso mestre no `sessionStorage` (expira ao fechar a aba).
 *
 * A perspectiva muda (o acesso mestre usa `isAdmin: false`), então os tokens de
 * preview cacheados para o admin são descartados — senão a visão do cliente
 * continuaria pedindo as miniaturas com privilégio de admin.
 */
export const setMasterToken = (token) => {
  limparTokensPreview();
  sessionStorage.setItem('masterToken', token);
};

/** Recupera o token de acesso mestre do sessionStorage. */
export const getMasterToken = () => sessionStorage.getItem('masterToken');

/**
 * Indica se há um token de acesso mestre ativo (existente e não expirado).
 * Não remove tokens inválidos: apenas informa o estado atual.
 */
export const isMasterAccessActive = () => {
  const token = getMasterToken();
  if (!token) return false;

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return Boolean(payload.isMasterAccess) && (!payload.exp || Date.now() / 1000 < payload.exp);
  } catch {
    return false;
  }
};

/** Remove o token de acesso mestre do sessionStorage. */
export const clearMasterToken = () => {
  // Voltamos à perspectiva do admin: o token de preview do cliente não vale mais.
  limparTokensPreview();
  sessionStorage.removeItem('masterToken');
};

/**
 * Retorna o ID efetivo do usuário atual:
 * - Se houver um master token ativo e válido → retorna o ID do cliente que está sendo visualizado
 * - Caso contrário → retorna o ID do usuário logado (getUserId)
 */
export const getEffectiveUserId = () => {
  const token = getMasterToken();
  if (!token) return getUserId();

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (payload.isMasterAccess && (!payload.exp || Date.now() / 1000 < payload.exp)) {
      return payload.id;
    }
  } catch {
    // ignora erros de decodificação
  }
  return getUserId();
};

/**
 * Verifica se o token de acesso mestre é válido e corresponde ao clienteId fornecido.
 * Retorna { valid: bool, payload: obj | null }
 */
export const checkMasterAccess = (clienteId) => {
  const token = getMasterToken();
  if (!token) return { valid: false, payload: null };

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));

    // Verifica se é realmente um token de acesso mestre
    if (!payload.isMasterAccess) return { valid: false, payload: null };

    // Verifica se o token é para o cliente correto
    if (String(payload.id) !== String(clienteId)) return { valid: false, payload: null };

    // Verifica se o token está expirado
    if (payload.exp && Date.now() / 1000 > payload.exp) {
      clearMasterToken();
      return { valid: false, payload: null };
    }

    return { valid: true, payload };
  } catch {
    return { valid: false, payload: null };
  }
};
